import { isNil, isString, isUndefined } from './lodash';
import { JSONParse } from './common';

const CONST = {
    expireTime: 24 * 60 * 60 * 1000
};

const serialize = (value: any) => {
    return JSON.stringify(value);
};

const deserialize = (value: any) => {
    if (!isString(value)) {
        return;
    }
    return JSONParse(value);
};

class StorageFactory {
    private storage: Storage;
    constructor(storage: Storage) {
        this.storage = storage;
    }

    /**
     * 某个item
     *
     * @param key
     * @param defaultValue
     */
    get = (key: string, defaultValue?: any) => {
        if (this.isExpiredItem(key)) {
            this.remove(key);
            return defaultValue;
        }
        let val = deserialize(this.storage.getItem(key));
        if (val && val.expire) {
            val = val.data;
        }
        return isUndefined(val) ? defaultValue : val;
    };

    /**
     * 设置某个item
     *
     * @param key
     * @param value
     * @param expire 过期时间，单位为天
     */
    set = (key: string, value?: any, expire?: number) => {
        let val = value;
        if (isUndefined(val)) {
            return this.remove(key);
        }
        if (expire) {
            val = {
                data: val,
                expire: Number(new Date()) + CONST.expireTime * expire
            };
        }
        try {
            this.storage.setItem(key, serialize(val));
        } catch (e) {
            this.clear(true);
            return;
        }
        return val;
    };

    /**
     * 删除某个item
     *
     * @param key
     */
    remove = (key: string) => {
        this.storage.removeItem(key);
    };

    /**
     * 清除localStorage
     *
     * @param expired   是否只清除过期的
     */
    clear = (expired: boolean) => {
        if (expired) {
            const len = this.storage.length;
            for (let i = 0; i < len; i++) {
                const key = this.storage.key(i) as string;
                if (this.isExpiredItem(key)) {
                    this.remove(key);
                }
            }
        } else {
            this.storage.clear();
        }
    };

    /**
     * 判断某个item是否已经过期
     *
     * @param key item键值
     */
    isExpiredItem = (key: string) => {
        const value = deserialize(this.storage.getItem(key));
        return value && value.expire && Date.now() > value.expire;
    };

    /**
     * 搜索某个key
     */
    search = (callback: (key: string) => void) => {
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key) {
                callback(key);
            }
        }
    };
}

class LocalStorageFactory extends StorageFactory {
    // 迁移规范
    getItemWithMigrate = (options: {
        oldKey: string;
        newKey: string;
        migrateFn?: (oldValue: any) => any;
        removeOldValue?: boolean;
        defaultValue?: any;
    }) => {
        const {
            oldKey,
            newKey,
            migrateFn = (v) => v,
            removeOldValue = true,
            defaultValue
        } = options;
        // 已经迁移过了，直接返回newKey存储的值
        const newValue = this.get(newKey);
        if (!isNil(newValue)) {
            return newValue;
        }
        const oldValue = this.get(oldKey);
        if (isNil(oldValue)) {
            return defaultValue;
        }

        const migratedValue = migrateFn(oldValue);
        // 将新值存储到新的key
        this.set(newKey, migratedValue);
        // 删除旧key
        if (removeOldValue) {
            this.remove(oldKey);
        }
        return migratedValue;
    };
}
const localStorageManager = new LocalStorageFactory(window.localStorage);
const sessionStorageManager = new StorageFactory(window.sessionStorage);

export { localStorageManager, sessionStorageManager };
